import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { http, fmtErr } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";

export default function GuestAccess() {
  const { key } = useParams();
  const { t } = useI18n();
  const nav = useNavigate();
  const [ev, setEv] = useState(null);
  const [phase, setPhase] = useState("phone"); // phone|otp|password
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [mockCode, setMockCode] = useState(null);
  const [partial, setPartial] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!key) return;
    (async () => {
      try {
        const { data } = await http.get(`/public/events/${key}`);
        setEv(data);
      } catch (e) { toast.error(fmtErr(e)); }
    })();
  }, [key]);

  if (!key) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Card className="p-8">
          <h1 className="font-serif text-3xl mb-2">{t.cta_guest}</h1>
          <p className="text-sm text-muted-foreground mb-6">Davet anahtarınızı girin.</p>
          <Input placeholder="ABCD1234" onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) nav(`/g/${e.target.value.trim()}`); }} data-testid="guest-key-input" />
        </Card>
      </div>
    );
  }

  const sendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/guest/otp/request", { phone, event_id: ev.id });
      setMockCode(data.mock_code);
      setPhase("otp");
      toast.success("OTP gönderildi (demo)");
    } catch (er) { toast.error(fmtErr(er)); }
    setLoading(false);
  };
  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/guest/otp/verify", { phone, event_id: ev.id, code: otp, display_name: name || phone });
      setPartial(data.partial_token);
      setPhase("password");
    } catch (er) { toast.error(fmtErr(er)); }
    setLoading(false);
  };
  const submitPw = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/guest/password", { password: pw }, { headers: { Authorization: `Bearer ${partial}` } });
      localStorage.setItem(`guest:${ev.id}`, data.guest_token);
      localStorage.setItem(`guest_name:${ev.id}`, name || phone);
      nav(`/g/${key}/live`);
    } catch (er) { toast.error(fmtErr(er)); }
    setLoading(false);
  };

  if (!ev) return <div className="mx-auto max-w-md px-4 py-16 text-muted-foreground">Yükleniyor...</div>;

  return (
    <div className="mx-auto max-w-md px-4 py-12">
      <div className="text-center mb-6">
        <div className="text-xs uppercase tracking-[0.3em] text-primary/80">{t.types?.[ev.event_type] || ev.event_type}</div>
        <h1 className="font-serif text-4xl mt-2 gold-text">{ev.title}</h1>
        <p className="text-sm text-muted-foreground mt-2">{fmtDate(ev.starts_at)}</p>
      </div>
      <Card className="p-6">
        {phase === "phone" && (
          <form onSubmit={sendOtp} className="space-y-4" data-testid="guest-otp-form">
            <div><Label>{t.display_name}</Label><Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="guest-name" /></div>
            <div><Label>{t.phone}</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+90 555 000 00 00" required data-testid="guest-phone" /></div>
            <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="guest-otp-send">{loading ? "..." : t.send_otp}</Button>
          </form>
        )}
        {phase === "otp" && (
          <form onSubmit={verify} className="space-y-4" data-testid="guest-verify-form">
            {mockCode && (
              <div className="p-3 rounded-lg border border-primary/30 bg-primary/10 text-center">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Demo OTP</div>
                <div className="font-mono text-3xl gold-text tracking-widest" data-testid="mock-otp">{mockCode}</div>
              </div>
            )}
            <div><Label>{t.otp_code}</Label><Input value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} data-testid="guest-otp-input" required /></div>
            <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="guest-otp-verify">{loading ? "..." : t.verify}</Button>
          </form>
        )}
        {phase === "password" && (
          <form onSubmit={submitPw} className="space-y-4" data-testid="guest-password-form">
            <div><Label>{t.enter_password}</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="guest-password" /></div>
            <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="guest-password-submit">{loading ? "..." : t.open_event}</Button>
          </form>
        )}
      </Card>
    </div>
  );
}
